import { Fragment } from "react";
import { Placeholder, Table } from "semantic-ui-react";

import CarrierCapacityShipmentMatchCapacityTableRow, {
  shipmentMatchColSpan,
} from "./CarrierCapacityShipmentMatchCapacityTableRow";
import { CarrierCapacityInfoDto } from "models/dto/CarrierCapacityInfoDto";
import CarrierCapacityShipmentMatchTableRow from "./CarrierCapacityShipmentMatchTableRow";
import {
  CapacityMatchStatus,
  SortedCapacityIds,
} from "../hooks/useGetCarrierCapacityShipmentMatches";
import { Option } from "models/Option";

import styles from "./CarrierCapacityShipmentMatchTable.module.scss";

type CarrierCapacityShipmentMatchTableProps<
  T extends "Capacity" | "Preferred Lane",
> = {
  type: T;
  sortedCapacityIds: SortedCapacityIds;
  capacityMatchMap: Record<number, CapacityMatchStatus>;
  capacities: CarrierCapacityInfoDto<T>[];
  totalCapacityShipments: number;
  equipmentOptions: Option<number>[];
  isLoadingCapacities: boolean;
};

const CarrierCapacityShipmentMatchTable = <
  T extends "Capacity" | "Preferred Lane",
>(
  props: CarrierCapacityShipmentMatchTableProps<T>
) => {
  return (
    <Table singleLine className={styles.shipmentMatchTable}>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell
            colSpan={shipmentMatchColSpan}
            className={styles.shipmentCount}
          >
            {`${props.type} - ${props.totalCapacityShipments} Shipment(s) Found`}
          </Table.HeaderCell>
        </Table.Row>
        <Table.Row>
          <Table.HeaderCell>Shipment ID</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>Equipment</Table.HeaderCell>
          <Table.HeaderCell>Route</Table.HeaderCell>
          <Table.HeaderCell>Route Appointment(s)</Table.HeaderCell>
          <Table.HeaderCell>Cap Quote</Table.HeaderCell>
          <Table.HeaderCell>Max Pay</Table.HeaderCell>
          <Table.HeaderCell>Customer(s)</Table.HeaderCell>
          <Table.HeaderCell>Item Count</Table.HeaderCell>
          <Table.HeaderCell>Items</Table.HeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {props.sortedCapacityIds.matches.map((capacityId) => {
          const capacity = props.capacities.find(
            (capacity) => capacity.id === capacityId
          );

          return capacity ? (
            <Fragment key={capacityId}>
              <CarrierCapacityShipmentMatchCapacityTableRow
                capacity={capacity}
                equipmentOptions={props.equipmentOptions}
              />

              {props.capacityMatchMap[capacityId].matches.map(
                (shipmentMatch) => (
                  <CarrierCapacityShipmentMatchTableRow
                    key={shipmentMatch.tmsShipmentId}
                    shipmentMatch={shipmentMatch}
                    capacity={capacity}
                  />
                )
              )}
            </Fragment>
          ) : null;
        })}

        {props.sortedCapacityIds.noMatches.length > 0 &&
        props.sortedCapacityIds.pending.length === 0
          ? props.sortedCapacityIds.noMatches.map((capacityId) => {
              const capacity = props.capacities.find(
                (capacity) => capacity.id === capacityId
              );

              return capacity ? (
                <CarrierCapacityShipmentMatchCapacityTableRow
                  key={capacityId}
                  capacity={capacity}
                  message="No Suggested Shipments"
                  equipmentOptions={props.equipmentOptions}
                />
              ) : null;
            })
          : null}

        {props.isLoadingCapacities ||
        props.sortedCapacityIds.pending.length > 0 ? (
          <Table.Row>
            {[...Array(shipmentMatchColSpan).keys()].map((value) => (
              <Table.Cell
                key={`${value}-loading-row`}
                className={styles.loadingRow}
              >
                <Placeholder fluid>
                  <Placeholder.Paragraph>
                    <Placeholder.Line />
                    <Placeholder.Line />
                  </Placeholder.Paragraph>
                </Placeholder>
              </Table.Cell>
            ))}
          </Table.Row>
        ) : null}
      </Table.Body>
    </Table>
  );
};

export default CarrierCapacityShipmentMatchTable;
